import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw, Users, CheckCircle, LogOut, Key, Trash2, Megaphone, X, Eye, EyeOff, Send, Radio, Database, MessageSquare, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPanel() {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal state
  const [resetModal, setResetModal] = useState(null); // clinic_name
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPw, setShowResetPw] = useState(false);
  const [promoModal, setPromoModal] = useState(null); // clinic object
  const [promoText, setPromoText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Broadcast state
  const [broadcastBanner, setBroadcastBanner] = useState('');
  const [bannerTheme, setBannerTheme] = useState('general');
  const [emailTemplate, setEmailTemplate] = useState('announcement');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailHeadline, setEmailHeadline] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailCtaLabel, setEmailCtaLabel] = useState('');
  const [emailCtaUrl, setEmailCtaUrl] = useState('');
  const [broadcastLoading, setBroadcastLoading] = useState(false);

  // Individual email modal state
  const [emailModal, setEmailModal] = useState(null); // clinic object
  const [indEmailSubject, setIndEmailSubject] = useState('');
  const [indEmailHeadline, setIndEmailHeadline] = useState('');
  const [indEmailBody, setIndEmailBody] = useState('');
  const [indEmailCtaLabel, setIndEmailCtaLabel] = useState('');
  const [indEmailCtaUrl, setIndEmailCtaUrl] = useState('');
  const [indEmailLoading, setIndEmailLoading] = useState(false);

  // Feedback state
  const [feedbackList, setFeedbackList] = useState([]);

  const adminFetch = (url, options = {}) => {
    const token = localStorage.getItem('admin_token');
    return fetch(`${BACKEND_URL}${url}`, {
      ...options,
      headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json', ...options.headers },
    });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [clinicsRes, statsRes, feedbackRes] = await Promise.all([
        adminFetch('/api/admin/clinics'),
        adminFetch('/api/admin/stats'),
        adminFetch('/api/admin/feedback'),
      ]);
      if (clinicsRes.status === 401 || statsRes.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }
      const clinicsData = await clinicsRes.json();
      const statsData = await statsRes.json();
      setClinics(clinicsData.clinics || []);
      setStats(statsData);
      if (feedbackRes.ok) {
        const fbData = await feedbackRes.json();
        setFeedbackList(fbData.feedback || []);
      }
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    loadData();
  }, []);

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setActionLoading(true);
    try {
      const res = await adminFetch('/api/admin/reset-password', {
        method: 'POST',
        body: JSON.stringify({ clinic_name: resetModal, new_password: resetPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Password reset for ${resetModal}`);
        setResetModal(null);
        setResetPassword('');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearSlot = async (clinicName) => {
    if (!window.confirm(`Clear registration slot for ${clinicName}? The clinic will need to register again.`)) return;
    setActionLoading(true);
    try {
      const res = await adminFetch('/api/admin/clear-slot', {
        method: 'POST',
        body: JSON.stringify({ clinic_name: clinicName }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Slot cleared for ${clinicName}`);
        loadData();
      } else {
        toast.error(data.detail || 'Failed to clear slot');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetPromo = async () => {
    setActionLoading(true);
    try {
      const res = await adminFetch('/api/admin/set-promo', {
        method: 'POST',
        body: JSON.stringify({ clinic_name: promoModal.clinic_name, message: promoText, theme: bannerTheme }),
      });
      if (res.ok) {
        toast.success(promoText ? `Promo set for ${promoModal.clinic_name}` : `Promo cleared for ${promoModal.clinic_name}`);
        setPromoModal(null);
        setPromoText('');
        setBannerTheme('general');
        loadData();
      }
    } catch {
      toast.error('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const handleBroadcastBanner = async (clear = false) => {
    const msg = clear ? '' : broadcastBanner.trim();
    if (!clear && !msg) { toast.error('Please enter a message'); return; }
    if (!clear && !window.confirm(`Set this banner for ALL ${clinics.filter(c => c.registered).length} registered clinics?`)) return;
    if (clear && !window.confirm('Clear the banner for ALL registered clinics?')) return;
    setBroadcastLoading(true);
    try {
      const res = await adminFetch('/api/admin/broadcast-promo', {
        method: 'POST',
        body: JSON.stringify({ message: msg, theme: clear ? 'general' : bannerTheme })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        if (clear) { setBroadcastBanner(''); setBannerTheme('general'); }
        loadData();
      }
    } catch { toast.error('Network error'); }
    finally { setBroadcastLoading(false); }
  };

  const handleBroadcastEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error('Subject and message body are required');
      return;
    }
    const count = clinics.filter(c => c.registered).length;
    if (!window.confirm(`Send this email to ALL ${count} registered clinics?`)) return;
    setBroadcastLoading(true);
    try {
      const res = await adminFetch('/api/admin/broadcast-email', {
        method: 'POST',
        body: JSON.stringify({
          subject: emailSubject, template: emailTemplate,
          headline: emailHeadline, body: emailBody,
          cta_label: emailCtaLabel, cta_url: emailCtaUrl,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Email sent to ${data.sent} clinics${data.failed > 0 ? ` (${data.failed} failed)` : ''}`);
        setEmailSubject(''); setEmailHeadline(''); setEmailBody('');
        setEmailCtaLabel(''); setEmailCtaUrl('');
      }
    } catch { toast.error('Network error'); }
    finally { setBroadcastLoading(false); }
  };

  const handleSendIndividualEmail = async () => {
    if (!indEmailSubject.trim() || !indEmailBody.trim()) {
      toast.error('Subject and message body are required');
      return;
    }
    if (!window.confirm(`Send this email to ${emailModal.clinic_name}?`)) return;
    setIndEmailLoading(true);
    try {
      const res = await adminFetch('/api/admin/send-email', {
        method: 'POST',
        body: JSON.stringify({
          clinic_name: emailModal.clinic_name,
          subject: indEmailSubject, template: 'announcement',
          headline: indEmailHeadline, body: indEmailBody,
          cta_label: indEmailCtaLabel, cta_url: indEmailCtaUrl,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setEmailModal(null);
        setIndEmailSubject(''); setIndEmailHeadline(''); setIndEmailBody('');
        setIndEmailCtaLabel(''); setIndEmailCtaUrl('');
      } else {
        toast.error(data.detail || 'Failed to send email');
      }
    } catch { toast.error('Network error'); }
    finally { setIndEmailLoading(false); }
  };

  const filtered = clinics.filter(c =>
    c.clinic_name.toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );
  const registered = filtered.filter(c => c.registered);
  const available = filtered.filter(c => !c.registered);

  const handleRemoveClinic = async (clinicName) => {
    if (!window.confirm(`Remove "${clinicName}" from the portal? This clinic will no longer appear in the registration list.`)) return;
    setActionLoading(true);
    try {
      const res = await adminFetch('/api/admin/remove-clinic', {
        method: 'POST',
        body: JSON.stringify({ clinic_name: clinicName }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        loadData();
      } else {
        toast.error(data.detail || 'Failed to remove clinic');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/20 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>Admin Panel</h1>
              <p className="text-sm text-muted-foreground">HOLONS Vet Portal Management</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadData} className="gap-2">
              <RefreshCw className="h-3.5 w-3.5" />Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/5">
              <LogOut className="h-3.5 w-3.5" />Logout
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Slots', value: stats.total_slots, icon: Users, color: 'text-primary' },
              { label: 'Registered', value: stats.registered, icon: CheckCircle, color: 'text-green-600' },
              { label: 'Available', value: stats.available, icon: Users, color: 'text-amber-500' },
              { label: 'Redemptions', value: stats.total_redemptions, icon: CheckCircle, color: 'text-blue-500' },
            ].map(s => (
              <Card key={s.label} className="border-0 shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <s.icon className={`h-8 w-8 ${s.color} opacity-80`} />
                  <div>
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>{s.value}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Broadcast to All Clinics */}
        <Card className="border-0 shadow-md mb-6 border-l-4 border-l-primary">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <Radio className="h-5 w-5 text-primary" />
              Broadcast to All Clinics
              <span className="text-xs font-normal text-muted-foreground ml-1">
                ({clinics.filter(c => c.registered).length} registered)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">

            {/* ── DASHBOARD BANNER ── */}
            <div className="space-y-3">
              <p className="text-sm font-semibold flex items-center gap-2">
                <Megaphone className="h-4 w-4 text-amber-500" />
                Dashboard Banner — appears on every clinic's dashboard
              </p>

              {/* Theme picker */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {[
                  { key: 'general',      emoji: '🎈', label: 'General',      bg: 'bg-amber-50',   border: 'border-amber-300',   ring: 'ring-amber-400'   },
                  { key: 'easter',       emoji: '🐣', label: 'Easter',       bg: 'bg-purple-50',  border: 'border-purple-300',  ring: 'ring-purple-400'  },
                  { key: 'christmas',    emoji: '🎄', label: 'Christmas',    bg: 'bg-red-50',     border: 'border-red-300',     ring: 'ring-red-400'     },
                  { key: 'vetday',       emoji: '🐾', label: 'Vet Day',      bg: 'bg-teal-50',    border: 'border-teal-300',    ring: 'ring-teal-400'    },
                  { key: 'newyear',      emoji: '🎆', label: 'New Year',     bg: 'bg-yellow-50',  border: 'border-yellow-300',  ring: 'ring-yellow-400'  },
                  { key: 'appreciation', emoji: '💙', label: 'Thank You',    bg: 'bg-blue-50',    border: 'border-blue-300',    ring: 'ring-blue-400'    },
                ].map(t => (
                  <button key={t.key} type="button"
                    onClick={() => setBannerTheme(t.key)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 cursor-pointer transition-all text-xs font-medium
                      ${bannerTheme === t.key ? `${t.bg} ${t.border} ring-2 ${t.ring} shadow-sm` : 'border-border hover:border-muted-foreground/30'}`}>
                    <span className="text-xl">{t.emoji}</span>
                    <span>{t.label}</span>
                  </button>
                ))}
              </div>

              <textarea value={broadcastBanner} onChange={e => setBroadcastBanner(e.target.value)}
                placeholder="Type your banner message here..."
                rows={2}
                className="w-full border border-border rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
                data-testid="broadcast-banner-input" />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handleBroadcastBanner(false)} disabled={broadcastLoading || !broadcastBanner.trim()} className="gap-2">
                  <Megaphone className="h-3.5 w-3.5" />Set Banner for All
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleBroadcastBanner(true)} disabled={broadcastLoading} className="gap-2 text-muted-foreground">
                  <X className="h-3.5 w-3.5" />Clear All Banners
                </Button>
              </div>
            </div>

            {/* ── EMAIL TEMPLATES ── */}
            <div className="border-t border-border/50 pt-5 space-y-4">
              <p className="text-sm font-semibold flex items-center gap-2">
                <Send className="h-4 w-4 text-blue-500" />
                Email All Clinics — branded template, sent to every registered clinic
              </p>

              {/* Single template — Announcement */}
              <div className="flex items-center gap-3 p-3 rounded-xl border-2 border-primary bg-primary/5">
                <span className="text-2xl">📢</span>
                <div>
                  <p className="text-sm font-semibold">Announcement</p>
                  <p className="text-xs text-muted-foreground">General news or updates</p>
                </div>
              </div>

              {/* Email fields */}
              <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                placeholder="Email subject line" className="text-sm" data-testid="broadcast-subject-input" />
              <Input value={emailHeadline} onChange={e => setEmailHeadline(e.target.value)}
                placeholder="Email headline — bold heading (optional)" className="text-sm" data-testid="broadcast-headline-input" />
              <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)}
                placeholder="Message body — use new lines for separate paragraphs..."
                rows={4}
                className="w-full border border-border rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
                data-testid="broadcast-email-input" />

              {/* Optional CTA */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input value={emailCtaLabel} onChange={e => setEmailCtaLabel(e.target.value)}
                  placeholder="Button label (optional, e.g. View Portal)" className="text-sm" />
                <Input value={emailCtaUrl} onChange={e => setEmailCtaUrl(e.target.value)}
                  placeholder="Button URL (optional)" className="text-sm" />
              </div>
              <p className="text-[11px] text-muted-foreground">HOLONS logo, branding and footer are added automatically.</p>

              <Button size="sm" onClick={handleBroadcastEmail}
                disabled={broadcastLoading || !emailSubject.trim() || !emailBody.trim()}
                className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="broadcast-email-btn">
                <Send className="h-3.5 w-3.5" />Send Email to All Clinics
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="mb-6">
          <Input
            placeholder="Search by clinic name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-md"
            data-testid="admin-search"
          />
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4,5].map(i => <div key={i} className="h-16 skeleton rounded-xl" />)}
          </div>
        ) : (
          <>
            {/* Registered Clinics */}
            <Card className="border-0 shadow-md mb-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Registered Clinics ({registered.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {registered.length === 0 ? (
                  <p className="text-sm text-muted-foreground px-6 pb-4">No registered clinics match your search.</p>
                ) : (
                  <div className="divide-y">
                    {registered.map((clinic, i) => (
                      <motion.div key={clinic.clinic_name}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                        className="px-6 py-4 flex flex-col md:flex-row md:items-center gap-3"
                        data-testid={`admin-clinic-row-${i}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm">{clinic.clinic_name}</p>
                          <p className="text-xs text-muted-foreground">{clinic.email}</p>
                          {clinic.registered_at && (
                            <p className="text-[11px] text-muted-foreground/60">
                              Registered {new Date(clinic.registered_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                          {clinic.promo_message && (
                            <div className="mt-1 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5">
                              <Megaphone className="h-3 w-3 text-amber-600" />
                              <span className="text-[11px] text-amber-700 truncate max-w-xs">{clinic.promo_message}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                          <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs"
                            onClick={() => { setEmailModal(clinic); setIndEmailSubject(''); setIndEmailHeadline(''); setIndEmailBody(''); setIndEmailCtaLabel(''); setIndEmailCtaUrl(''); }}
                            data-testid={`email-btn-${i}`}>
                            <Send className="h-3.5 w-3.5" />Email
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs"
                            onClick={() => { setPromoModal(clinic); setPromoText(clinic.promo_message || ''); }}
                            data-testid={`promo-btn-${i}`}>
                            <Megaphone className="h-3.5 w-3.5" />Promo
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs"
                            onClick={() => { setResetModal(clinic.clinic_name); setResetPassword(''); }}
                            data-testid={`reset-btn-${i}`}>
                            <Key className="h-3.5 w-3.5" />Reset PW
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs text-destructive border-destructive/30 hover:bg-destructive/5"
                            onClick={() => handleClearSlot(clinic.clinic_name)}
                            data-testid={`clear-btn-${i}`}>
                            <Trash2 className="h-3.5 w-3.5" />Clear Slot
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Available Slots */}
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  <Users className="h-5 w-5 text-amber-500" />
                  Available Slots ({available.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {available.map(clinic => (
                    <Badge key={clinic.clinic_name} variant="outline" className="text-xs py-1 px-3 text-muted-foreground">
                      {clinic.clinic_name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Clinic Feedback */}
        <Card className="border-0 shadow-md mt-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                <MessageSquare className="h-5 w-5 text-primary" />
                Clinic Feedback
                {feedbackList.filter(f => !f.read).length > 0 && (
                  <span className="text-xs font-normal bg-primary text-white px-2 py-0.5 rounded-full ml-1">
                    {feedbackList.filter(f => !f.read).length} new
                  </span>
                )}
              </CardTitle>
              {feedbackList.some(f => !f.read) && (
                <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs"
                  onClick={async () => {
                    await adminFetch('/api/admin/feedback/mark-read', { method: 'POST', body: '{}' });
                    loadData();
                    toast.success('All feedback marked as read');
                  }}>
                  <CheckCircle className="h-3.5 w-3.5" />Mark All Read
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {feedbackList.length === 0 ? (
              <p className="text-sm text-muted-foreground">No feedback received yet.</p>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {feedbackList.map((fb, i) => (
                  <div key={`${fb.clinic_email}-${fb.created_at}-${i}`} className={`p-4 rounded-xl border transition-all ${fb.read ? 'bg-background border-border' : 'bg-primary/5 border-primary/20'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-semibold">{fb.clinic_name}</span>
                          {!fb.read && <span className="text-[10px] font-medium bg-primary text-white px-1.5 py-0.5 rounded">NEW</span>}
                          <span className="text-xs text-muted-foreground px-2 py-0.5 rounded-md bg-muted">
                            {(fb.category || 'general').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          {fb.rating && (
                            <div className="flex gap-0.5">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className={`h-3 w-3 ${s <= fb.rating ? 'fill-amber-400 text-amber-400' : 'text-border'}`} />
                              ))}
                            </div>
                          )}
                        </div>
                        <p className="text-sm">{fb.message}</p>
                        <p className="text-[11px] text-muted-foreground mt-1">{fb.clinic_email} — {fb.created_at ? new Date(fb.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data Management */}
        <DataManagement adminFetch={adminFetch} clinics={clinics} />
      </div>

      {/* Reset Password Modal */}
      <AnimatePresence>
        {resetModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg" style={{ fontFamily: 'Fredoka, sans-serif' }}>Reset Password</h3>
                <button onClick={() => setResetModal(null)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{resetModal}</p>
              <div className="relative mb-4">
                <Input type={showResetPw ? 'text' : 'password'} placeholder="New password (min 6 chars)"
                  value={resetPassword} onChange={e => setResetPassword(e.target.value)}
                  className="pr-10" data-testid="reset-pw-input" />
                <button type="button" onClick={() => setShowResetPw(!showResetPw)}
                  className="absolute right-3 top-3 text-muted-foreground">
                  {showResetPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleResetPassword} disabled={actionLoading} className="flex-1" data-testid="reset-confirm-btn">
                  {actionLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
                <Button variant="outline" onClick={() => setResetModal(null)} className="px-4">Cancel</Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Promo Message Modal */}
      <AnimatePresence>
        {promoModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg" style={{ fontFamily: 'Fredoka, sans-serif' }}>Promo Message</h3>
                <button onClick={() => setPromoModal(null)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
              </div>
              <p className="text-sm text-muted-foreground mb-3">{promoModal.clinic_name}</p>

              {/* Theme picker */}
              <div className="grid grid-cols-3 gap-1.5 mb-3">
                {[
                  { key: 'general',      emoji: '🎈', label: 'General'    },
                  { key: 'easter',       emoji: '🐣', label: 'Easter'     },
                  { key: 'christmas',    emoji: '🎄', label: 'Christmas'  },
                  { key: 'vetday',       emoji: '🐾', label: 'Vet Day'    },
                  { key: 'newyear',      emoji: '🎆', label: 'New Year'   },
                  { key: 'appreciation', emoji: '💙', label: 'Thank You'  },
                ].map(t => (
                  <button key={t.key} type="button"
                    onClick={() => setPromoText(prev => { setBannerTheme(t.key); return prev; })}
                    className={`flex items-center gap-1.5 p-2 rounded-lg border text-xs font-medium cursor-pointer transition-all
                      ${bannerTheme === t.key ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-primary/30'}`}>
                    <span>{t.emoji}</span><span>{t.label}</span>
                  </button>
                ))}
              </div>

              <textarea value={promoText} onChange={e => setPromoText(e.target.value)} rows={3}
                placeholder="Enter a promo message for this clinic (leave empty to clear)..."
                className="w-full border border-border rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 mb-3"
                data-testid="promo-text-input" />
              <p className="text-xs text-muted-foreground mb-4">This message will appear as a themed banner on the clinic's dashboard.</p>
              <div className="flex gap-2">
                <Button onClick={handleSetPromo} disabled={actionLoading} className="flex-1" data-testid="promo-confirm-btn">
                  {actionLoading ? 'Saving...' : promoText ? 'Set Promo' : 'Clear Promo'}
                </Button>
                <Button variant="outline" onClick={() => setPromoModal(null)} className="px-4">Cancel</Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Individual Email Modal */}
      <AnimatePresence>
        {emailModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg" style={{ fontFamily: 'Fredoka, sans-serif' }}>Send Email</h3>
                <button onClick={() => setEmailModal(null)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
              </div>
              <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
                <Send className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-blue-900">{emailModal.clinic_name}</p>
                  <p className="text-xs text-blue-600">{emailModal.email}</p>
                </div>
              </div>
              <div className="space-y-3">
                <Input value={indEmailSubject} onChange={e => setIndEmailSubject(e.target.value)}
                  placeholder="Email subject line" className="text-sm" data-testid="ind-email-subject" />
                <Input value={indEmailHeadline} onChange={e => setIndEmailHeadline(e.target.value)}
                  placeholder="Headline — bold heading (optional)" className="text-sm" data-testid="ind-email-headline" />
                <textarea value={indEmailBody} onChange={e => setIndEmailBody(e.target.value)}
                  placeholder="Message body — use new lines for paragraphs..."
                  rows={5}
                  className="w-full border border-border rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
                  data-testid="ind-email-body" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={indEmailCtaLabel} onChange={e => setIndEmailCtaLabel(e.target.value)}
                    placeholder="Button label (optional)" className="text-sm" />
                  <Input value={indEmailCtaUrl} onChange={e => setIndEmailCtaUrl(e.target.value)}
                    placeholder="Button URL (optional)" className="text-sm" />
                </div>
                <p className="text-[11px] text-muted-foreground">HOLONS branding and footer are added automatically.</p>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleSendIndividualEmail}
                  disabled={indEmailLoading || !indEmailSubject.trim() || !indEmailBody.trim()}
                  className="flex-1 gap-2 bg-blue-600 hover:bg-blue-700" data-testid="ind-email-send-btn">
                  {indEmailLoading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  {indEmailLoading ? 'Sending...' : `Send to ${emailModal.clinic_name}`}
                </Button>
                <Button variant="outline" onClick={() => setEmailModal(null)} className="px-4">Cancel</Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}


// Data Management sub-component with per-type, per-clinic deletion and toggle lock
function DataManagement({ adminFetch, clinics }) {
  const [unlocked, setUnlocked] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState('');
  const [deleting, setDeleting] = useState('');
  const unlockTimer = React.useRef(null);

  const registeredClinics = clinics.filter(c => c.registered);

  const handleUnlock = () => {
    setUnlocked(true);
    if (unlockTimer.current) clearTimeout(unlockTimer.current);
    unlockTimer.current = setTimeout(() => setUnlocked(false), 30000);
  };

  React.useEffect(() => {
    return () => { if (unlockTimer.current) clearTimeout(unlockTimer.current); };
  }, []);

  const handleDelete = async (dataType, label) => {
    const scope = selectedClinic || 'ALL clinics';
    if (!window.confirm(`Delete ${label} for ${scope}? This cannot be undone.`)) return;
    setDeleting(dataType);
    try {
      const body = { data_type: dataType };
      if (selectedClinic) body.clinic_name = selectedClinic;
      const res = await adminFetch('/api/admin/clear-data', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setDeleting('');
    }
  };

  const dataTypes = [
    { key: 'collection_requests', label: 'Collection Requests', icon: '📋' },
    { key: 'redemptions', label: 'Cash Redemptions', icon: '💰' },
    { key: 'product_orders', label: 'Product Orders', icon: '📦' },
    { key: 'material_requests', label: 'Material Requests', icon: '📄' },
    { key: 'webhook_events', label: 'Webhook Events', icon: '🔔' },
    { key: 'feedback', label: 'Feedback', icon: '💬' },
  ];

  return (
    <Card className="border border-destructive/20 shadow-sm mt-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-destructive" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <Database className="h-4 w-4" />
            Data Management
          </CardTitle>
          <button
            type="button"
            onClick={unlocked ? () => setUnlocked(false) : handleUnlock}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
              unlocked
                ? 'bg-destructive/10 text-destructive border border-destructive/30'
                : 'bg-muted text-muted-foreground border border-border hover:border-destructive/30'
            }`}
            data-testid="data-mgmt-lock"
          >
            {unlocked ? (
              <><Trash2 className="h-3.5 w-3.5" />Enabled — auto-locks in 30s</>
            ) : (
              <><Key className="h-3.5 w-3.5" />Enable Deletion</>
            )}
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Clinic selector */}
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-muted-foreground flex-shrink-0">Scope:</label>
          <select
            value={selectedClinic}
            onChange={e => setSelectedClinic(e.target.value)}
            className="flex-1 text-sm border border-border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
            data-testid="data-mgmt-clinic-select"
          >
            <option value="">All Clinics</option>
            {registeredClinics.map(c => (
              <option key={c.clinic_name} value={c.clinic_name}>{c.clinic_name}</option>
            ))}
          </select>
        </div>

        {/* Data type buttons */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {dataTypes.map(dt => (
            <button
              key={dt.key}
              type="button"
              disabled={!unlocked || deleting === dt.key}
              onClick={() => handleDelete(dt.key, dt.label)}
              className={`flex items-center gap-2 p-3 rounded-xl border text-left text-sm transition-all ${
                unlocked
                  ? 'border-destructive/30 hover:bg-destructive/5 hover:border-destructive/50 cursor-pointer text-destructive'
                  : 'border-border text-muted-foreground/50 cursor-not-allowed opacity-60'
              }`}
              data-testid={`clear-${dt.key}`}
            >
              <span className="text-base">{dt.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {deleting === dt.key ? 'Deleting...' : `Clear ${dt.label}`}
                </p>
              </div>
            </button>
          ))}
        </div>

        {!unlocked && (
          <p className="text-xs text-muted-foreground text-center">
            Click "Enable Deletion" to unlock the delete buttons
          </p>
        )}
        {unlocked && selectedClinic && (
          <p className="text-xs text-destructive text-center font-medium">
            Deleting data for: {selectedClinic} only
          </p>
        )}
        {unlocked && !selectedClinic && (
          <p className="text-xs text-destructive text-center font-medium">
            Deleting data for: ALL clinics
          </p>
        )}
      </CardContent>
    </Card>
  );
}
